import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Sunrise, Sunset } from "lucide-react";
import { toast } from "sonner";

const CLASSES = [
  { id: "6", label: "Class 6" },
  { id: "7", label: "Class 7" },
  { id: "8", label: "Class 8" },
  { id: "9", label: "Class 9" },
  { id: "10", label: "Class 10" },
];

export default function DailyQuiz() {
  const [classLevel, setClassLevel] = useState("10");
  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);
  const [wrongIdx, setWrongIdx] = useState(null);
  const { user, loginWithGoogle } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/quiz/daily?class_level=${classLevel}`).then((r) => {
      setQuiz(r.data); setStep(0); setAnswers([]); setDone(false);
    });
  }, [classLevel]);

  const finalise = async (finalAnswers) => {
    setDone(true);
    if (!user) return;
    const score = finalAnswers.reduce((s, a, idx) => s + (quiz.answer_key[idx] === a ? 1 : 0), 0);
    // topic breakdown from questions + answers
    const perTopic = {};
    quiz.questions.forEach((q, i) => {
      const t = q.topic || "General";
      if (!perTopic[t]) perTopic[t] = { correct: 0, total: 0 };
      perTopic[t].total += 1;
      if (quiz.answer_key[i] === finalAnswers[i]) perTopic[t].correct += 1;
    });
    const topic_breakdown = Object.entries(perTopic).map(([topic, v]) => ({ topic, ...v }));
    try {
      const r = await api.post("/quiz/submit", {
        class_level: classLevel,
        slot: quiz.slot,
        score,
        total: quiz.questions.length,
        date_str: quiz.date,
        topic_breakdown,
      });
      toast.success(`Streak: ${r.data.streak} 🔥`);
    } catch {}
  };

  const choose = async (i) => {
    if (!quiz) return;
    const correct = quiz.answer_key[step] === i;
    if (!correct) { setWrongIdx(i); setTimeout(() => setWrongIdx(null), 400); }
    const next = [...answers, i];
    setAnswers(next);
    if (step + 1 >= quiz.questions.length) { finalise(next); }
    else { setStep(step + 1); }
  };

  if (!quiz) return <div className="text-zinc-500">Loading quiz…</div>;
  const q = quiz.questions[step];
  const score = answers.reduce((s, a, idx) => s + (quiz.answer_key[idx] === a ? 1 : 0), 0);

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 fade-up">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-1 flex items-center gap-2">
            {quiz.slot === "morning" ? <Sunrise className="w-3.5 h-3.5" /> : <Sunset className="w-3.5 h-3.5" />}
            {quiz.slot} quiz · {quiz.date}
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Daily Quiz</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {CLASSES.map((c) => (
            <button
              key={c.id}
              data-testid={`quiz-class-${c.id}`}
              onClick={() => setClassLevel(c.id)}
              className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border ${
                classLevel === c.id ? "bg-sky-400 text-slate-900 border-yellow-400 font-semibold" : "border-white/10 text-zinc-400"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {!done ? (
        <div className={`rounded-lg border border-white/10 bg-[#152040] p-8 ${wrongIdx !== null ? "shake" : ""}`} data-testid="quiz-card">
          <div className="flex justify-between text-xs text-zinc-500 mb-4">
            <span>Question {step + 1} of {quiz.questions.length}</span>
            <span className="font-mono text-pink-300">{q.topic}</span>
          </div>
          <div className="font-display text-2xl font-bold mb-8 leading-tight">{q.q}</div>
          <div className="grid gap-2">
            {q.options.map((opt, i) => (
              <button
                key={i}
                data-testid={`quiz-option-${i}`}
                onClick={() => choose(i)}
                className="text-left rounded-md border border-white/10 bg-[#0b1428] px-4 py-3 hover:border-yellow-400 hover:bg-yellow-400/5 transition-colors font-mono text-sm"
              >
                <span className="text-yellow-300 mr-3">{String.fromCharCode(65 + i)}.</span>{opt}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-white/10 bg-[#152040] p-8 fade-up" data-testid="quiz-result">
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Result</div>
          <div className="font-display text-5xl font-bold mb-2 gradient-streak">{score}/{quiz.questions.length}</div>
          <div className="text-zinc-400 mb-8">
            {score === quiz.questions.length ? "Perfect run. Board-ready energy." : score >= 3 ? "Nice work — keep the streak alive." : "Room to grow — try the AI Tutor for weak topics."}
          </div>
          {!user && (
            <div className="rounded-md border border-yellow-400/30 bg-orange-300/10 text-yellow-100 px-4 py-3 mb-6 text-sm">
              Sign in with Google to save this score and start a streak.
              <Button size="sm" className="ml-3 bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full" onClick={loginWithGoogle} data-testid="quiz-signin-btn">Sign in</Button>
            </div>
          )}
          <div className="space-y-2 mb-8">
            {quiz.questions.map((qq, idx) => {
              const yes = quiz.answer_key[idx] === answers[idx];
              return (
                <div key={idx} className="flex items-start gap-3 text-sm">
                  {yes ? <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-400 mt-0.5" />}
                  <div>
                    <div className="text-zinc-200">{qq.q}</div>
                    {!yes && (
                      <div className="text-xs text-zinc-500 font-mono mt-0.5">Correct: {qq.options[quiz.answer_key[idx]]}</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3">
            <Button data-testid="quiz-retake-btn" onClick={() => { setStep(0); setAnswers([]); setDone(false); }} className="bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold">
              Try another class
            </Button>
            {user && (
              <Button variant="outline" data-testid="quiz-dashboard-btn" onClick={() => nav("/dashboard")} className="rounded-full border-white/15 bg-transparent">
                Go to dashboard
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
